import { createContext, useContext, useState, useMemo } from "react";

const AUTH_SESSION_KEY = "proponent_auth_session";
const SESSION_DURATION_MS = 8 * 60 * 60 * 1000; // 8 hours

const API_BASE_URL = process.env.REACT_APP_BACKEND_BASE_URL;

const AuthContext = createContext(null);

function readSession() {
  try {
    const raw = sessionStorage.getItem(AUTH_SESSION_KEY);
    if (!raw) return null;
    const session = JSON.parse(raw);
    if (Date.now() > session.expiry) {
      sessionStorage.removeItem(AUTH_SESSION_KEY);
      return null;
    }
    return session;
  } catch {
    return null;
  }
}

export function getAuthToken() {
  try {
    const raw = sessionStorage.getItem(AUTH_SESSION_KEY);
    if (!raw) return null;
    const session = JSON.parse(raw);
    if (Date.now() > session.expiry) return null;
    return session.token ?? null;
  } catch {
    return null;
  }
}

function saveSession(token) {
  const session = { expiry: Date.now() + SESSION_DURATION_MS, token };
  sessionStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(session));
}

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(
    () => process.env.REACT_APP_SKIP_AUTH === "true" || readSession() !== null
  );

  /**
   * Step 1 – verify username + password against the backend.
   *
   * Returns Promise<{
   *   success: boolean,
   *   requiresSetup?: boolean,   // true on first login (no otp_secret in DB yet)
   *   totpSecret?: string,        // base32 secret to show as QR code (setup only)
   *   totpUri?: string,           // otpauth:// URI for QR code (setup only)
   *   error?: string,
   * }>
   */
  async function verifyCredentials(username, password) {
    if (!API_BASE_URL) {
      return { success: false, error: "Backend URL is not configured (REACT_APP_BACKEND_BASE_URL)." };
    }
    try {
      const res = await fetch(`${API_BASE_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        return { success: false, error: data.error || "Invalid username or password." };
      }
      return {
        success: true,
        requiresSetup: data.requires_setup === true,
        totpSecret: data.totp_secret,
        totpUri: data.totp_uri,
      };
    } catch {
      return { success: false, error: "Could not reach the server. Please try again." };
    }
  }

  /**
   * Step 2a (returning users) – verify the TOTP code against the backend.
   * Returns Promise<{ success: boolean, error?: string }>
   */
  async function verifyTotp(totpCode, username) {
    if (!API_BASE_URL) {
      return { success: false, error: "Backend URL is not configured (REACT_APP_BACKEND_BASE_URL)." };
    }
    try {
      const res = await fetch(`${API_BASE_URL}/auth/verify-totp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, totp_code: totpCode }),
      });
      const data = await res.json();
      if (!res.ok) {
        return { success: false, error: data.error || "Invalid or expired authentication code." };
      }
      saveSession(data.token);
      setIsAuthenticated(true);
      return { success: true };
    } catch {
      return { success: false, error: "Could not reach the server. Please try again." };
    }
  }

  /**
   * Step 2b (first login) – confirm the newly scanned TOTP secret and save it.
   * Returns Promise<{ success: boolean, error?: string }>
   */
  async function setupTotp(totpCode, username, totpSecret) {
    if (!API_BASE_URL) {
      return { success: false, error: "Backend URL is not configured (REACT_APP_BACKEND_BASE_URL)." };
    }
    try {
      const res = await fetch(`${API_BASE_URL}/auth/setup-totp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, totp_code: totpCode, totp_secret: totpSecret }),
      });
      const data = await res.json();
      if (!res.ok) {
        return { success: false, error: data.error || "Invalid or expired authentication code." };
      }
      saveSession(data.token);
      setIsAuthenticated(true);
      return { success: true };
    } catch {
      return { success: false, error: "Could not reach the server. Please try again." };
    }
  }

  function logout() {
    sessionStorage.removeItem(AUTH_SESSION_KEY);
    setIsAuthenticated(false);
  }

  const contextValue = useMemo(
    () => ({ isAuthenticated, verifyCredentials, verifyTotp, setupTotp, logout }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAuthenticated]
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
